import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { map, Observable } from 'rxjs';
import { BaseService } from './base.service';
import * as ApiProduct from '../interfaces/api/product';
import * as ApiCategory from '../interfaces/api/category';
import * as ApiPagination from '../interfaces/api/pagination';
import { MHProduct } from '../interfaces/product';
import { ProductTransformer } from '../utils/transformers/product.transformer';
import { Coordinate } from '../interfaces/location';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends BaseService {
  public orderBy: string;
  public keyword: string;
  public minPrice: string;
  public maxPrice: string;
  public idCategory: string;
  public idSubCategory: number;
  public idfeeds: string;
  public idTags: string;
  public idWarehouse: string;
  public is_preorder: string;
  public refreshFilter: boolean;
  public fromHome: boolean;

  private urlProduct = `${this.apiUrl}/product`;
  constructor(private http: HttpClient, private platform: Platform, private productTransformer: ProductTransformer) {
    super();
  }

  public clearParam() {
    this.orderBy = null;
    this.keyword = null;
    this.minPrice = null;
    this.maxPrice = null;
    this.idCategory = null;
    this.idSubCategory = null;
    this.idfeeds = null;
    this.idTags = null;
    this.idWarehouse = null;
    this.is_preorder = null;
    this.refreshFilter = false;
    this.fromHome = false;
  }

  getProductCollectionList() {
    return this.http.get<ApiProduct.ResponseBody>(`${this.apiUrl}/product_collection?with_detail=1`);
  }

  getProductReview(idProduct: string) {
    return this.http.get<ApiProduct.ResponseBodyProductReview>(`${this.apiUrl}/product/review/${idProduct}`);
  }

  getProductBestSelling() {
    return this.http.get<ApiProduct.ResponseBodyProductBestSelling>(`${this.apiUrl}/product_collection/best_selling`);
  }

  getProductRelatedList(idProduct: string | number) {
    return this.http.get<ApiProduct.ResponseBodyRelated>(`${this.apiUrl}/product-related/${idProduct}`, {
      params: {
        // no_loading: true,
      },
    });
  }

  getProductWarehouseList(idProduct: string | number) {
    return this.http.get<ApiProduct.ResponseBodyProductWarehouse>(`${this.apiUrl}/stock?id_mh_product=${idProduct}`, {
      params: {
        // no_loading: true,
      },
    });
  }

  getWarehouseList() {
    return this.http.get<ApiProduct.ResponseBodyWarehouse>(`${this.apiUrl}/warehouse`);
  }

  getWarehouseCities() {
    const url = new URL(`${this.apiUrl}/warehouse/city`);
    return this.http.get<ApiProduct.ResponseBodyCities>(url.toString());
  }

  getCategories(order_field?: string, order_type?: 'desc' | 'asc') {
    const url = new URL(`${this.apiUrl}/category`);

    if (order_field) {
      url.searchParams.set('order_field', String(order_field));
    }

    if (order_type) {
      url.searchParams.set('order_type', String(order_type));
    }

    return this.http.get<ApiCategory.GetCategoriesResponse>(url.toString());
  }

  getSubCategories(parentId: number, order_field?: string, order_type?: 'desc' | 'asc') {
    const url = new URL(`${this.apiUrl}/category/children/${parentId}`);

    if (order_field) {
      url.searchParams.set('order_field', String(order_field));
    }

    if (order_type) {
      url.searchParams.set('order_type', String(order_type));
    }

    return this.http.get<ApiCategory.GetCategoriesResponse>(url.toString());
  }

  /* getCategoryFilter() {
    return this.http.get<ApiCategory.ResponseBodyCategory>(`${this.apiUrl}/category`);
  }

  getSubCategories() {
    return this.http.get<ApiCategory.ResponseBodySubCategory>(`${this.apiUrl}/subcategory/homepage`);
  } */

  getTagsFilter() {
    return this.http.get<ApiCategory.ResponseBodyTag>(`${this.apiUrl}/tag`);
  }

  getSingleProduct(idProduct: string, cityId: string) {
    const url = new URL(`${this.apiUrl}/product/detail/${idProduct}`);

    if (cityId) {
      url.searchParams.set('city', String(cityId));
    }

    return this.http.get<ApiProduct.ResponseBodySingle>(url.toString());
  }

  getMostOrderedProducts() {
    return this.http.get<ApiProduct.ResponseBodyMostOrdered>(`${this.apiUrl}/product_most_ordered`);
  }

  getProductList(
    perPage?: number,
    page?: number,
    is_recommended?: 0 | 1,
    id_category?: number,
    discount?: number,
    name?: string,
    city?: number,
    wishlist?: boolean,
    id_brand?: number,
    tags?: number[],
  ) {
    const params = new URLSearchParams();

    if (perPage) {
      params.append('perPage', String(perPage));
    } else {
      params.append('perPage', '6');
    }

    if (page) {
      params.append('page', String(page));
    } else {
      params.append('page', '1');
    }

    if (city) {
      params.append('city', String(city));
    }

    if (is_recommended) {
      params.append('is_recommended', String(is_recommended));
    }

    if (id_category) {
      params.append('id_category', String(id_category));
    }

    if (discount) {
      params.append('discount', String(discount));
    }

    if (name) {
      params.append('name', String(name));
    }

    if (wishlist) {
      params.append('wishlist', String(wishlist));
    }

    if (id_brand) {
      params.append('id_brand', String(id_brand));
    }

    if (tags && tags.length > 0) {
      for (const tag of tags) {
        params.append('tag[]', String(tag));
      }
    }

    const url = new URL(`${this.apiUrl}/product`);
    url.search = params.toString();

    return this.http.get<ApiProduct.ResponseBodyProducts>(url.toString());
  }

  getWishlist({
    page,
    perPage,
    sort,
    city_id,
    sort_by,
  }: {
    perPage?: number;
    page?: number;
    sort?: 'ASC' | 'DESC';
    sort_by?: 'name' | 'date';
    city_id?: number;
  }) {
    const url = new URL(`${this.apiUrl}/wishlist`);

    if (perPage) {
      url.searchParams.set('perPage', String(perPage));
    } else {
      url.searchParams.set('perPage', '6');
    }

    if (page) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.set('page', '1');
    }

    if (sort) {
      url.searchParams.set('sort', sort ?? 'DESC');
    }

    if (sort_by) {
      url.searchParams.set('sort_by', sort_by ?? 'date');
    }

    if (city_id) {
      url.searchParams.set('city_id', city_id.toString());
    }

    return this.http.get<ApiProduct.ResponseBodyGetWishlist>(url.toString());
  }

  storeWishlist(payload: { id_mh_product: number; id_mh_city: number }) {
    return this.http.post<ApiProduct.ResponseBodyPostWishlist>(`${this.apiUrl}/wishlist`, payload);
  }

  deleteProductWishlist(productId: number, cityId: number) {
    return this.http.delete<ApiProduct.ResponseBodyDeleteWishlist>(`${this.apiUrl}/wishlist/${productId}/${cityId}`);
  }

  /* //[MUNGKIN BISA DI GUNAKAN UNTUK PAGINATION PRODUK]
  getProductList(
    // queryParams: ApiProduct.queryProducts
    queryParams?: string,
  ): Observable<ApiPagination.ResponseBody<MHProduct>> {
    const url = new URL(this.urlProduct + queryParams);

    // const url = new URL(`${this.urlProduct}`);
    // url.searchParams.set('page', queryParams.page?.toString());
    // if (queryParams.per_page) {
    //   url.searchParams.set('perPage', queryParams.per_page?.toString());
    // }
    // if (queryParams.search) {
    //   url.searchParams.set('keyword', queryParams.search);
    // }
    // if (queryParams.sub_category_id) {
    //   url.searchParams.set('sub_category_id', queryParams.sub_category_id?.toString());
    // }
    // if (queryParams.category_id && !queryParams.sub_category_id) {
    //   url.searchParams.set('category_id', queryParams.category_id?.toString());
    // }
    // if (queryParams.collection_id) {
    //   url.searchParams.set('keyword', queryParams.collection_id?.toString());
    // }
    // if (queryParams.sort_by) {
    //   url.searchParams.set('sort_by', queryParams.sort_by?.toString());
    // }
    // if (queryParams.selected_tags?.length > 0) {
    //   queryParams.selected_tags?.forEach(x => {
    //     url.searchParams.set('selected_tags[]', x.toString());
    //   })
    // }
    // if (queryParams.min_price) {
    //   url.searchParams.set('min_price', queryParams.min_price?.toString());
    // }
    // if (queryParams.max_price) {
    //   url.searchParams.set('max_price', queryParams.max_price?.toString());
    // }
    // if (queryParams.id_mh_warehouse) {
    //   url.searchParams.set('id_mh_warehouse', queryParams.id_mh_warehouse?.toString());
    // }
    // if (queryParams.is_preorder) {
    //   url.searchParams.set('is_preorder', queryParams.is_preorder?.toString());
    // }

    // url.searchParams.set('page', queryParams.toString());

    // if (queryParams) {
    //   url.searchParams.set('prefix', queryParams);
    // }
    // console.log(url.toString())
    //[NB: Seharusnya return seperti ini, tetapi API memberikan level headernya bukan seperti ApiPagination.ResponseBody]
    // return this.http.get<ApiPagination.ResponseBody<ApiProduct.MHProduct>>(url.toString()).pipe(
    //   map(pagination => ({
    //     ...pagination,
    //     data: pagination.data.map(item => this.productTransformer.transform(item)),
    //   })),
    // );

    // return this.http.get<any>(url.toString()).pipe(
    //   map(pagination => ({
    //     ...pagination,
    //     data: pagination.products.data.map(item => this.productTransformer.transform(item)),
    //   })),
    // );
    return this.http.get<ApiProduct.ResponseBodyProducts>(url.toString()).pipe(
      map(pagination => ({
        ...pagination.products,
      })),
    );
  } */

  getNearestWarehouse(coordinate: Coordinate) {
    return this.http.post<ApiProduct.ResponseBodyNearestWarehouse>(`${this.apiUrl}/warehouse/nearest`, {
      lat: coordinate.latitude,
      lng: coordinate.longitude,
      no_loading: true,
    });
  }
}
